import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios, { CancelToken } from "axios";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import classNames from "classnames";
import moment from "moment";

import { StatusCode } from "api/protocols";
import { UserActive } from "api/rpc";
import { GetClient } from "api/rpc/2024-04/customer/client";
import { GetClientGuest } from "api/rpc/2024-04/guest/client";
import {
  DeleteFacilityFavourite,
  GetFacility,
  PostFacilityFavourite,
} from "api/rpc/2024-04/customer/facility";
import { GetFacilityGuest, GetGuestFacilityStatus } from "api/rpc/2024-04/guest/facility";
import { GetGuestTournament } from "api/rpc/2024-04/guest/tournament/tournament";
import { GetCustomerTournament } from "api/rpc/2024-04/customer/tournament";
import { GetGuestLeague } from "api/rpc/2024-04/guest/league";
import { GetCustomerLeague } from "api/rpc/2024-04/customer/league";
import { GetGuestHospitalityEvent } from "api/rpc/2024-04/guest/hospitalityEvent";
import { GetCustomerHospitalityEvent } from "api/rpc/2024-04/customer/hospitalityEvent";

import { dequeue, enqueue, showError, showSuccess } from "redux/actions/ui";
import { clientPortalUpdate } from "redux/actions/clientPortal";
import { TFacilityStatus } from "redux/reducers/models/facility";
import { sortFacilities } from "redux/reducers/clientPortal";
import { ILeague } from "redux/reducers/models/league";
import { ITournament } from "redux/reducers/models/tournament";
import { IEvent } from "redux/reducers/models/event";

import { formatDate, sortObj } from "helpers/Helpers";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import useModal from "hooks/modals/useModal";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import useOutsideAlerter from "hooks/useOutsideAlerter/useOutsideAlerter";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";

import { ClientPortalNavigation } from "../ClientPortalNavigation";
import ClientFacilityDropdown from "./ClientFacilityDropdown";
import FacilityStatusInfo from "./FacilityStatusInfo";
import CalendarGridDay from "components/calendar/CalendarGridDay";
import Calendar from "components/calendar/Calendar";
import { Badge } from "components/badge/Badge";
import DataTable from "components/dataTable/DataTable";
import { getPortalEventTypeLabel } from "../Events/PortalEventCard";

import "./clientPortalCalendar.scss";
import "../../../../secure/facility/events/calendar/eventsCalendar/eventsCalendar.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faCalendar, faClock, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { faCircle } from "@fortawesome/pro-solid-svg-icons";
import { mapPortalEventsByDate, TPortalCalendarEvent, TPortalCalendarEventType } from "./portalCalendarEvents";

interface IParams {
  clientShortName: string;
  facilityShortName?: string;
}

const DESKTOP_EVENT_ROW_HEIGHT = 16;
const MOBILE_EVENT_ROW_HEIGHT = 14;
const EVENT_ROW_GAP = 2;

function eventRowsHeight(rowCount: number, rowHeight: number) {
  if (rowCount <= 0) {
    return 0;
  }
  return rowCount * rowHeight + (rowCount - 1) * EVENT_ROW_GAP;
}

function countVisibleEvents(availableHeight: number, eventCount: number, rowHeight: number) {
  if (eventCount <= 0 || availableHeight < rowHeight) {
    return 0;
  }

  if (eventRowsHeight(eventCount, rowHeight) <= availableHeight) {
    return eventCount;
  }

  let count = eventCount - 1;
  while (count >= 0 && eventRowsHeight(count + 1, rowHeight) > availableHeight) {
    count -= 1;
  }

  return Math.max(count, 0);
}

export default function ClientPortalCalendar() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { clientShortName, facilityShortName } = useParams<IParams>();
  const clientPortalStore = useAppSelector(store => store.clientPortalStore);

  const [calendarDay, setCalendarDay] = useState(new Date());
  const [facilityStatuses, setFacilityStatuses] = useState<TFacilityStatus[] | undefined>([]);
  const [statusLoadTrigger, setStatusLoadTrigger] = useState(true);
  const [leagues, setLeagues] = useState<ILeague[]>([]);
  const [tournaments, setTournaments] = useState<ITournament[]>([]);
  const [hospitalityEvents, setHospitalityEvents] = useState<IEvent[]>([]);
  const [eventDetailsHover, setEventDetailsHover] = useState<{
    event: TPortalCalendarEvent;
    date: string;
    rect: DOMRect | null;
  }>({
    event: null,
    date: "",
    rect: null,
  });
  const [moreEventsPopover, setMoreEventsPopover] = useState<{
    date: string;
    events: TPortalCalendarEvent[];
    rect: DOMRect;
  } | null>(null);
  const moreEventsPopoverRef = useRef<HTMLDivElement>(null);

  const windowSize = useWindowSize();
  const isMobile = windowSize.width <= MOBILE_WIDTH;

  const dayRefs = useRef<HTMLDivElement[]>([]);

  useOutsideAlerter(moreEventsPopoverRef, () => {
    setMoreEventsPopover(null);
    setEventDetailsHover({ event: null, date: "", rect: null });
  });

  const {
    state: statusModal,
    updateModal: updateStatusModal,
    closeModal: closeStatusModal,
  } = useModal({
    activeDate: null as string,
    ref: null as HTMLDivElement,
    statusData: [] as TFacilityStatus[],
    type: null as "updates" | "status",
  });

  useEffect(() => {
    if (!clientPortalStore?.client) {
      void initializeClientAndFacilities();
    }
  }, []);

  async function initializeClientAndFacilities() {
    let currentActiveUser = !!clientPortalStore?.user;
    let selectedClient = clientPortalStore?.client;
    let selectedFacility = clientPortalStore?.selectedFacility;
    let clientRes;
    let facilityRes;

    dispatch(enqueue());
    const activeUserRes = await UserActive(false);
    if (activeUserRes?.status === StatusCode.OK) {
      currentActiveUser = true;
    }

    if (currentActiveUser) {
      clientRes = await GetClient({ short_name: clientShortName }, false);
    } else {
      clientRes = await GetClientGuest({ short_name: clientShortName }, false);
    }
    if (clientRes.status !== StatusCode.OK || clientRes?.data?.length === 0) {
      dispatch(dequeue());
      dispatch(showError(t("Error loading client")));
      return;
    }
    selectedClient = clientRes?.data[0];
    if (currentActiveUser) {
      facilityRes = await GetFacility({ client_id: selectedClient?.id, order_by: "long_name" }, false);
    } else {
      facilityRes = await GetFacilityGuest({ client_id: selectedClient?.id, order_by: "long_name" }, false);
    }

    if (facilityRes?.status !== StatusCode.OK || facilityRes?.data?.length === 0) {
      dispatch(dequeue());
      dispatch(showError(t("Error loading facilities")));
      return;
    }

    const sortedFacilities = sortFacilities(facilityRes?.data);
    selectedFacility = selectedFacility
      ? sortedFacilities?.find(facility => facility?.id === selectedFacility?.id)
      : facilityShortName
        ? sortedFacilities?.find(facility => facility?.short_name === facilityShortName)
        : sortedFacilities[0];
    if (!selectedFacility && sortedFacilities?.length > 0) {
      selectedFacility = sortedFacilities[0];
    }
    selectedClient = { ...selectedClient, facilities: sortedFacilities };
    dispatch(
      clientPortalUpdate({
        client: selectedClient,
        selectedFacility,
      }),
    );
    dispatch(dequeue());
  }

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (clientPortalStore.selectedFacility && statusLoadTrigger) {
      void loadGuestFacilityStatus(clientPortalStore.selectedFacility?.id, calendarDay.getMonth(), source.token);
    }
    return () => source.cancel();
  }, [clientPortalStore.selectedFacility, statusLoadTrigger, calendarDay]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (clientPortalStore.selectedFacility) {
      void loadCalendarEvents(clientPortalStore.selectedFacility.id, calendarDay, source.token);
    }
    return () => source.cancel();
  }, [clientPortalStore.selectedFacility?.id, calendarDay.getFullYear(), calendarDay.getMonth()]);

  async function loadGuestFacilityStatus(facilityId: number, month: number, token?: CancelToken) {
    if (facilityStatuses !== undefined) {
      setFacilityStatuses(undefined);
    }
    const res = await GetGuestFacilityStatus(
      { facility_id: facilityId, month: month + 1 },
      token ? false : true,
      token,
    );
    if (token && token.reason) {
      return;
    }

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading facility details.")));
    }

    const nextStatuses: TFacilityStatus[] =
      res.status !== StatusCode.OK ? [] : ((res.data ?? []) as TFacilityStatus[]);
    setFacilityStatuses(nextStatuses);
    if (statusLoadTrigger) {
      setStatusLoadTrigger(false);
    }
  }

  async function loadCalendarEvents(facilityId: number, date: Date, token?: CancelToken) {
    const userActive = !!clientPortalStore?.user;
    const params = {
      facility_id: facilityId,
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      limit: 100,
      offset: 0,
    };

    const [tournamentRes, leagueRes, hospitalityRes] = await Promise.all([
      userActive ? GetCustomerTournament(params, false, token) : GetGuestTournament(params, false, token),
      userActive ? GetCustomerLeague(params, false, token) : GetGuestLeague(params, false, token),
      userActive
        ? GetCustomerHospitalityEvent(params, false, token)
        : GetGuestHospitalityEvent(params, false, token),
    ]);

    if (token?.reason) {
      return;
    }

    if (tournamentRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting tournaments")));
    }
    if (leagueRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting leagues")));
    }
    if (hospitalityRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting hospitality events")));
    }

    setTournaments(tournamentRes.data ?? []);
    setLeagues(leagueRes.data ?? []);
    setHospitalityEvents(hospitalityRes.data ?? []);
  }

  async function handleFavouriteChange(type: "remove" | "add") {
    if (type === "add") {
      const res = await PostFacilityFavourite({ facility_id: clientPortalStore.selectedFacility?.id }, true);
      if (res.status !== StatusCode.OK) {
        dispatch(showError(t("Error adding course to favourites.")));
        return;
      }
    }

    if (type === "remove") {
      const res = await DeleteFacilityFavourite({ facility_id: clientPortalStore.selectedFacility?.id }, true);
      if (res.status !== StatusCode.OK) {
        dispatch(showError(t("Error removing course from favourites.")));
        return;
      }
    }

    dispatch(showSuccess(t("Successfully updated course favourites.")));
    updateCourseFavouritesList();
  }

  function handleFacilityChange(id: number) {
    const foundFacility = clientPortalStore.client?.facilities.find(fac => fac.id === id);
    if (foundFacility) {
      dispatch(clientPortalUpdate({ selectedFacility: foundFacility }));
      setStatusLoadTrigger(true);
    }
  }

  function handleDateChange(date: Date) {
    if (date.getMonth() !== calendarDay.getMonth() || date.getFullYear() !== calendarDay.getFullYear()) {
      setStatusLoadTrigger(true);
    }

    setCalendarDay(date);
  }

  function updateCourseFavouritesList() {
    const facilities = clientPortalStore.client?.facilities;
    if (!facilities?.length) {
      dispatch(showError(t("Error updating course favourites list.")));
      return;
    }
    const updatedFacilities = [...facilities];
    const index = updatedFacilities.findIndex(fac => fac.id === clientPortalStore.selectedFacility?.id);

    if (index !== -1) {
      updatedFacilities[index] = { ...updatedFacilities[index], favourite: !updatedFacilities[index].favourite };
      const sorted = sortFacilities([...updatedFacilities]);
      const client = { ...clientPortalStore.client, facilities: sorted };
      dispatch(clientPortalUpdate({ client, selectedFacility: updatedFacilities[index] }));
    } else {
      dispatch(showError(t("Error updating course favourites list.")));
    }
  }

  function handleCalendarCellClick(data: TFacilityStatus[], dateOfMonth: number) {
    if (data == null || data.length === 0) {
      return;
    }

    updateStatusModal({
      isOpen: true,
      activeDate: data[0].date,
      ref: dayRefs.current[dateOfMonth],
      statusData: sortObj<TFacilityStatus>(data, "label"),
      type: "status",
    });
  }

  const eventsByDate = useMemo(
    () => mapPortalEventsByDate({ leagues, tournaments, hospitalityEvents }),
    [leagues, tournaments, hospitalityEvents],
  );

  const eventCounts = useMemo(() => {
    return {
      leagues: leagues.length,
      tournaments: tournaments.length,
      weddings: hospitalityEvents.filter(event => event.type === "wedding").length,
      banquets: hospitalityEvents.filter(event => event.type === "banquet").length,
      other: hospitalityEvents.filter(event => event.type !== "wedding" && event.type !== "banquet").length,
    };
  }, [leagues, tournaments, hospitalityEvents]);

  function getTypeGroup(type: TPortalCalendarEventType) {
    return type === "league" || type === "tournament" ? t("Golf") : t("Hospitality");
  }

  function registrationPath(event: TPortalCalendarEvent) {
    const shortName = clientPortalStore?.selectedFacility?.short_name;
    if (!shortName || !event.handle) {
      return null;
    }
    if (event.type === "tournament") {
      return `/tournament/${shortName}/${event.handle}/home`;
    }
    if (event.type === "league") {
      return `/league/${shortName}/${event.handle}/home`;
    }
    return null;
  }

  return (
    <div className="client-portal-calendar-page">
      <ClientPortalNavigation showNavOptions />
      <div className="client-portal-calendar-facility">
        <ClientFacilityDropdown
          selectedFacility={clientPortalStore?.selectedFacility}
          facilityList={clientPortalStore.client?.facilities}
          onChange={handleFacilityChange}
          handleFavourite={clientPortalStore.user ? type => handleFavouriteChange(type) : undefined}
        />
      </div>
      <div className="client-portal-calendar-body">
        <Calendar
          fillMonthViewport={false}
          selectedDate={calendarDay}
          setSelectedDate={date => handleDateChange(date)}
          loading={facilityStatuses === undefined}
          renderGridItem={(dateOfMonth: number, formattedDate: string) => {
            const data = (facilityStatuses ?? []).filter(val => val.date === formattedDate);
            const dayEvents = eventsByDate[formattedDate] ?? [];

            return (
              <CalendarGridDay
                key={formattedDate}
                dayOfMonth={dateOfMonth}
                dayOfMonthClick={() => (isMobile ? handleCalendarCellClick(data, dateOfMonth) : undefined)}
                gridClick={() => setCalendarDay(new Date(formattedDate.replace(/-/g, "/")))}
                selected={formatDate(new Date(formattedDate.replace(/-/g, "/"))) === formatDate(calendarDay)}
                today={formatDate(new Date(formattedDate.replace(/-/g, "/"))) === formatDate(new Date())}
                ref={el => (dayRefs.current[dateOfMonth] = el)}
                mobileView={isMobile}
              >
                {dayEvents.length > 0 && (
                  <PortalCalendarDayEvents
                    events={dayEvents}
                    hasStatus={data.length > 0}
                    isMobile={isMobile}
                    onHover={(event, rect) => {
                      if (isMobile) {
                        return;
                      }
                      setEventDetailsHover({ event, date: formattedDate, rect });
                    }}
                    onHoverEnd={() => setEventDetailsHover({ event: null, date: "", rect: null })}
                    onOpen={event => {
                      const path = registrationPath(event);
                      if (path) {
                        window.open(path);
                      }
                    }}
                    canOpen={event => !!registrationPath(event)}
                    moreLabel={count => `+${count} ${t("more...")}`}
                    onShowMore={rect => {
                      setEventDetailsHover({ event: null, date: "", rect: null });
                      setMoreEventsPopover(current =>
                        current?.date === formattedDate ? null : { date: formattedDate, events: dayEvents, rect },
                      );
                    }}
                  />
                )}
                {data.length > 0 && (
                  <div
                    className={`client-portal-calendar-badge-grouping${isMobile ? " mobile" : ""}${
                      statusModal.isOpen && statusModal.activeDate === formattedDate && statusModal.type === "status"
                        ? " active"
                        : ""
                    }`}
                    onClick={() => (isMobile ? handleCalendarCellClick(data, dateOfMonth) : undefined)}
                  >
                    <Badge
                      className={`status-badge event${
                        statusModal.isOpen && statusModal.activeDate === formattedDate && statusModal.type === "status"
                          ? " active"
                          : ""
                      }`}
                      size="small"
                      onClick={() => (!isMobile ? handleCalendarCellClick(data, dateOfMonth) : undefined)}
                    >
                      {isMobile ? <FontAwesomeIcon icon={faBell} /> : t("Daily Updates")}
                    </Badge>
                  </div>
                )}
              </CalendarGridDay>
            );
          }}
        />
        {(eventCounts.leagues > 0 ||
          eventCounts.tournaments > 0 ||
          eventCounts.weddings > 0 ||
          eventCounts.banquets > 0 ||
          eventCounts.other > 0) && (
            <div className="ui-events-calendar-legend">
              <p className="text-semibold">{t("Legend")}</p>
              {(eventCounts.leagues > 0 || eventCounts.tournaments > 0) && (
                <div className="flex items-center gap-2">
                  {eventCounts.leagues > 0 && (
                    <Badge outline size="medium" colour="#026AA2" backgroundColour="#FFFFFF">
                      <FontAwesomeIcon icon={faCircle} style={{ fontSize: "6px" }} className="mr-2" />
                      {eventCounts.leagues} {eventCounts.leagues === 1 ? t("League") : t("Leagues")}
                    </Badge>
                  )}
                  {eventCounts.tournaments > 0 && (
                    <Badge outline size="medium" colour="#027A48" backgroundColour="#FFFFFF">
                      <FontAwesomeIcon icon={faCircle} style={{ fontSize: "6px" }} className="mr-2" />
                      {eventCounts.tournaments} {eventCounts.tournaments === 1 ? t("Tournament") : t("Tournaments")}
                    </Badge>
                  )}
                </div>
              )}
              {(eventCounts.leagues > 0 || eventCounts.tournaments > 0) &&
                (eventCounts.weddings > 0 || eventCounts.banquets > 0 || eventCounts.other > 0) && (
                  <div className="ui-events-calendar-legend-divider" />
                )}
              {(eventCounts.weddings > 0 || eventCounts.banquets > 0 || eventCounts.other > 0) && (
                <div className="flex items-center gap-2">
                  {eventCounts.weddings > 0 && (
                    <Badge outline size="medium" colour="#C11574" backgroundColour="#FFFFFF">
                      <FontAwesomeIcon icon={faCircle} style={{ fontSize: "6px" }} className="mr-2" />
                      {eventCounts.weddings} {eventCounts.weddings === 1 ? t("Wedding") : t("Weddings")}
                    </Badge>
                  )}
                  {eventCounts.banquets > 0 && (
                    <Badge outline size="medium" colour="#B93815" backgroundColour="#FFFFFF">
                      <FontAwesomeIcon icon={faCircle} style={{ fontSize: "6px" }} className="mr-2" />
                      {eventCounts.banquets} {eventCounts.banquets === 1 ? t("Banquet") : t("Banquets")}
                    </Badge>
                  )}
                  {eventCounts.other > 0 && (
                    <Badge outline size="medium" colour="#5925DC" backgroundColour="#FFFFFF">
                      <FontAwesomeIcon icon={faCircle} style={{ fontSize: "6px" }} className="mr-2" />
                      {eventCounts.other} {t("Other")}
                    </Badge>
                  )}
                </div>
              )}
            </div>
        )}
      </div>

      {moreEventsPopover &&
        createPortal(
          <div
            ref={moreEventsPopoverRef}
            className="ui-events-calendar-popover client-portal-more-events-portal"
            style={getMoreEventsPortalStyle(moreEventsPopover.rect, moreEventsPopover.events.length)}
          >
            <div className="ui-events-calendar-popover-header">
              <div>
                <span className="ui-events-calendar-popover-primary">{t("All events")}</span>
                <br />
                <span className="ui-events-calendar-popover-secondary">
                  {moment(moreEventsPopover.date).format("dddd")} • {moment(moreEventsPopover.date).format("ll")}
                </span>
              </div>
              <FontAwesomeIcon
                onClick={e => {
                  e.stopPropagation();
                  setMoreEventsPopover(null);
                  setEventDetailsHover({ event: null, date: "", rect: null });
                }}
                className="ui-events-calendar-popover-secondary cursor-pointer"
                icon={faXmark}
              />
            </div>
            <div className="ui-events-calendar-popover-body">
              {moreEventsPopover.events.map(event => (
                <div
                  key={`${event.type}-${event.id}`}
                  className={classNames("ui-events-calendar-events-row-wrapper", {
                    [`ui-events-calendar-events-row-wrapper-${event.type}`]: event.type,
                  })}
                >
                  <div
                    className={classNames(
                      "ui-events-calendar-events-row ui-events-calendar-events-row-start-date ui-events-calendar-events-row-end-date",
                      {
                        [`ui-events-calendar-events-row-${event.type}`]: event.type,
                        "cursor-pointer": !!registrationPath(event),
                      },
                    )}
                    onMouseEnter={e => {
                      if (isMobile) {
                        return;
                      }
                      setEventDetailsHover({
                        event,
                        date: moreEventsPopover.date,
                        rect: e.currentTarget.getBoundingClientRect(),
                      });
                    }}
                    onMouseLeave={() => setEventDetailsHover({ event: null, date: "", rect: null })}
                    onClick={e => {
                      e.stopPropagation();
                      const path = registrationPath(event);
                      if (path) {
                        window.open(path);
                      }
                    }}
                  >
                    <span className="ui-events-calendar-events-row-text-overflow">{event.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>,
          document.body,
        )}

      {eventDetailsHover.event &&
        eventDetailsHover.rect &&
        createPortal(
          <div
            className={classNames(
              "ui-events-calendar-popover ui-events-calendar-hover client-portal-event-hover-portal",
              `ui-events-calendar-events-row-wrapper-${eventDetailsHover.event.type}`,
            )}
            style={getEventHoverPortalStyle(eventDetailsHover.rect)}
          >
            <div className="ui-events-calendar-hover-header">
              <span className="ui-events-calendar-hover-primary">{eventDetailsHover.event.name}</span>
              <span className="ui-events-calendar-hover-secondary">
                {getTypeGroup(eventDetailsHover.event.type)} • {getPortalEventTypeLabel(eventDetailsHover.event.type, t)}
              </span>
            </div>
            <div className="ui-events-calendar-hover-body">
              {eventDetailsHover.event.start_time && (
                <div className="ui-events-calendar-hover-row">
                  <FontAwesomeIcon icon={faClock} />{" "}
                  <span>{moment(eventDetailsHover.event.start_time, "HH:mm").format("LT")}</span>
                </div>
              )}
              <div className="ui-events-calendar-hover-row">
                <FontAwesomeIcon icon={faCalendar} />{" "}
                <span>
                  {moment(eventDetailsHover.event.start).format("ll")}
                  {eventDetailsHover.event.end && eventDetailsHover.event.end !== eventDetailsHover.event.start
                    ? ` - ${moment(eventDetailsHover.event.end).format("ll")}`
                    : ""}
                </span>
              </div>
            </div>
          </div>,
          document.body,
        )}

      <FacilityStatusInfo
        isOpen={statusModal.isOpen}
        close={closeStatusModal}
        dateString={statusModal.activeDate}
        gridRefElement={!isMobile ? statusModal.ref : undefined}
        iFrame={false}
        title={t("Daily Updates")}
      >
        {statusModal.statusData
          .filter(val => val.label.toLowerCase().includes("note"))
          .map(status => (
            <div key={status.id} className="client-portal-status-note">
              <p className="note-label">{t("Note")}</p>
              <p className="note-value">{status.value}</p>
            </div>
          ))}
        <DataTable
          columns={[
            { label: "", width: "50%" },
            { label: "", width: "50%" },
          ]}
          hideHeader
          className="client-portal-calendar"
        >
          {statusModal.statusData
            .filter(val => !val.label.toLowerCase().includes("note"))
            .map((status, index) => (
              <tr key={status.id} className="client-portal-status-popup-table-row">
                <td style={index === 0 ? { borderTop: "none" } : undefined}>{status.label}</td>
                <td style={index === 0 ? { borderTop: "none" } : undefined}>{status.value}</td>
              </tr>
            ))}
        </DataTable>
      </FacilityStatusInfo>
    </div>
  );
}

function getMoreEventsPortalStyle(rect: DOMRect, eventCount: number): React.CSSProperties {
  const width = 260;
  const gap = 6;
  const padding = 8;
  const estimatedHeight = Math.min(360, 72 + eventCount * 28);
  const spaceBelow = window.innerHeight - rect.bottom - padding;
  const spaceAbove = rect.top - padding;
  const showAbove = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;
  const left = Math.min(Math.max(rect.left, padding), window.innerWidth - width - padding);
  const top = showAbove
    ? Math.max(padding, rect.top - gap - estimatedHeight)
    : Math.min(rect.bottom + gap, window.innerHeight - padding - 80);

  return { top, left, width };
}

function getEventHoverPortalStyle(rect: DOMRect): React.CSSProperties {
  const popoverWidth = 300;
  const popoverHeight = 160;
  const gap = 6;
  const viewportPadding = 8;
  const showAbove = rect.bottom + gap + popoverHeight > window.innerHeight - viewportPadding;
  const left = Math.min(
    Math.max(rect.left + rect.width / 2, popoverWidth / 2 + viewportPadding),
    window.innerWidth - popoverWidth / 2 - viewportPadding,
  );

  return {
    top: showAbove ? rect.top - gap : rect.bottom + gap,
    left,
    transform: showAbove ? "translate(-50%, -100%)" : "translate(-50%, 0)",
  };
}

function PortalCalendarDayEvents(props: {
  events: TPortalCalendarEvent[];
  hasStatus: boolean;
  isMobile: boolean;
  onHover: (event: TPortalCalendarEvent, rect: DOMRect) => void;
  onHoverEnd: () => void;
  onOpen: (event: TPortalCalendarEvent) => void;
  canOpen: (event: TPortalCalendarEvent) => boolean;
  moreLabel: (count: number) => string;
  onShowMore: (rect: DOMRect) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(props.events.length);
  const rowHeight = props.isMobile ? MOBILE_EVENT_ROW_HEIGHT : DESKTOP_EVENT_ROW_HEIGHT;

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return;
    }

    const measure = () => {
      setVisibleCount(countVisibleEvents(element.clientHeight, props.events.length, rowHeight));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [props.events.length, props.hasStatus, props.isMobile, rowHeight]);

  const visibleEvents = props.events.slice(0, visibleCount);
  const overflowCount = props.events.length - visibleEvents.length;

  return (
    <div
      ref={containerRef}
      className={classNames("client-portal-calendar-events", {
        "client-portal-calendar-events-with-status": props.hasStatus,
      })}
    >
      {visibleEvents.map(event => (
        <div
          key={`${event.type}-${event.id}`}
          className={classNames("ui-events-calendar-events-row-wrapper", {
            [`ui-events-calendar-events-row-wrapper-${event.type}`]: event.type,
          })}
        >
          <div
            className={classNames(
              "ui-events-calendar-events-row ui-events-calendar-events-row-start-date ui-events-calendar-events-row-end-date",
              {
                [`ui-events-calendar-events-row-${event.type}`]: event.type,
                "cursor-pointer": props.canOpen(event),
              },
            )}
            onMouseEnter={e => props.onHover(event, e.currentTarget.getBoundingClientRect())}
            onMouseLeave={props.onHoverEnd}
            onClick={e => {
              e.stopPropagation();
              props.onOpen(event);
            }}
          >
            <span className="ui-events-calendar-events-row-text-overflow">{event.name}</span>
          </div>
        </div>
      ))}
      {overflowCount > 0 && (
        <div
          className="ui-events-calendar-events-row ui-events-calendar-events-row-more cursor-pointer"
          onMouseDown={e => e.stopPropagation()}
          onClick={e => {
            e.stopPropagation();
            props.onShowMore(e.currentTarget.getBoundingClientRect());
          }}
        >
          <span className="ui-events-calendar-events-row-text-overflow">{props.moreLabel(overflowCount)}</span>
        </div>
      )}
    </div>
  );
}
